if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.observer?.unobserve(this);
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.loadRecommendations();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      // Cart items can carry complementary products assigned to different
      // parent products (Search & Discovery is configured per product, not
      // per cart). One recommendations request only reflects one product,
      // so every unique product in the cart gets its own request; results
      // are then merged and deduped by product id — Shopify already drops
      // products that are themselves in the cart from each response.
      async loadRecommendations() {
        const productIds = (this.dataset.productIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        if (!productIds.length) return;

        const responses = await Promise.all(
          productIds.map((id) =>
            fetch(`${this.dataset.url}&product_id=${id}&section_id=${this.dataset.sectionId}`)
              .then((response) => response.text())
              .catch(() => '')
          )
        );

        const track = this.querySelector('.cart-drawer__recommendations-track');
        const inner = this.querySelector('.cart-drawer__recommendations-inner');
        if (!track || !inner) return;

        const seenProductIds = new Set();
        const cards = [];

        responses.forEach((text) => {
          if (!text) return;
          const html = document.createElement('div');
          html.innerHTML = text;
          html.querySelectorAll('.cart-recommendation-card').forEach((card) => {
            const cardProductId = card.querySelector('input[name="product-id"]')?.value;
            if (!cardProductId || seenProductIds.has(cardProductId)) return;
            seenProductIds.add(cardProductId);
            cards.push(card);
          });
        });

        if (!cards.length) return;

        cards.forEach((card, index) => {
          const slide = document.createElement('div');
          slide.id = `Slide-CartDrawerRecommendations-${index + 1}`;
          slide.className = 'cart-drawer__recommendations-slide slider__slide';
          slide.setAttribute('role', 'group');
          slide.appendChild(card);
          track.appendChild(slide);
        });

        inner.removeAttribute('hidden');
      }
    }
  );
}

if (!customElements.get('cart-recommendations-slider')) {
  customElements.define(
    'cart-recommendations-slider',
    class CartRecommendationsSlider extends HTMLElement {
      static DRAG_THRESHOLD = 40;

      constructor() {
        super();
        this.track = this.querySelector('.cart-drawer__recommendations-track');

        if (!this.track) return;

        this.isDown = false;
        this.dragged = false;
        this.startX = 0;
        this.lastX = 0;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);

        this.track.addEventListener('pointerdown', this.onPointerDown);
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        this.track.addEventListener('click', this.onClickCapture, true);
      }

      onPointerDown(event) {
        if (event.pointerType !== 'mouse') return;
        this.isDown = true;
        this.dragged = false;
        this.startX = event.clientX;
        this.lastX = event.clientX;
        this.track.classList.add('is-dragging');
      }

      // Deliberately doesn't move the track in real time — a 1:1 cursor
      // follow reads as "stuck to the cursor" rather than a deck of cards.
      // The swipe is only measured here; onPointerUp resolves it to a single
      // discrete step (or no movement at all below the threshold).
      onPointerMove(event) {
        if (!this.isDown) return;
        this.lastX = event.clientX;
        if (Math.abs(this.lastX - this.startX) > 5) this.dragged = true;
      }

      onPointerUp() {
        if (!this.isDown) return;
        this.isDown = false;
        this.track.classList.remove('is-dragging');

        const delta = this.lastX - this.startX;
        if (Math.abs(delta) > CartRecommendationsSlider.DRAG_THRESHOLD) {
          this.goToAdjacentSlide(delta < 0 ? 1 : -1);
        }

        // Allow the click suppression to run before it's reset.
        requestAnimationFrame(() => {
          this.dragged = false;
        });
      }

      goToAdjacentSlide(direction) {
        const slides = Array.from(this.track.children);
        if (!slides.length) return;

        const trackRect = this.track.getBoundingClientRect();
        const currentCenter = this.track.scrollLeft + trackRect.width / 2;

        let currentIndex = 0;
        let closestDistance = Infinity;
        slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - currentCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            currentIndex = index;
          }
        });

        const targetIndex = Math.min(Math.max(currentIndex + direction, 0), slides.length - 1);
        const target = slides[targetIndex];
        this.track.scrollTo({
          left: target.offsetLeft - (trackRect.width - target.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }

      onClickCapture(event) {
        if (this.dragged) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  );
}
